import { ICP_MUST_MISSING_STARS_MAX, ICP_STAR_FLOORS, compareIcpRanks, type IcpReadState, type IcpStars, type PoolMatchRow, type PoolMatchView } from '@nest-recruit/shared';

// ADR 0062 §5, mockup M5: a pool member is matched by the job's ideal candidate. Facts show at once; a match number appears only once
// their CV is read, so nobody is ranked on lines Jev has not seen. Pure, so the wording and the order are unit-tested.

/** `not_needed`: the profile has no Jev-read line, so the facts are the whole match. */
const MATCHED_STATES: readonly IcpReadState[] = ['read', 'not_needed'];

export function isMatched(row: Pick<PoolMatchRow, 'readState'>): boolean {
  return row.readState !== null && MATCHED_STATES.includes(row.readState);
}

/** The stars a match earns (§2.4), capped when a must-have is missing; the pool rows carry only the match and that flag. */
export function starsOfMatch(match: number, isMissingMustHave: boolean): IcpStars {
  const floor = ICP_STAR_FLOORS.find((entry) => match >= entry.from && (!isMissingMustHave || entry.stars <= ICP_MUST_MISSING_STARS_MAX));
  return floor ? floor.stars : 1;
}

/** Matched people first, in the profile's order; people not matched yet after them, best on the facts first. */
export function profileMatchOrder(rows: readonly PoolMatchRow[]): readonly PoolMatchRow[] {
  return rows
    .map((row, order) => ({ row, order }))
    .sort((a, b) => Number(isMatched(b.row)) - Number(isMatched(a.row)) || compareIcpRanks({ rankKey: a.row.rankKey, order: a.order }, { rankKey: b.row.rankKey, order: b.order }))
    .map(({ row }) => row);
}

export interface PoolReadNotice {
  readonly title: string;
  readonly body: string;
  readonly isReading: boolean;
  /** "Read their CVs with Jev · about 9¢": offered while there is somebody to read. */
  readonly buttonLabel: string | null;
}

const READ_BODY = 'Pool people are matched to the ideal candidate. Facts show at once; a match number appears once their CV is read, so nobody is ranked on lines Jev hasn’t seen.';

/** The banner over the list: what has been read, what Jev is reading now, and the button to read more. Null for a job ranked by the criteria. */
export function poolReadNoticeOf(jevRead: NonNullable<PoolMatchView['jevRead']>): PoolReadNotice {
  if (jevRead.isReading) {
    return { title: `Jev is reading ${jevRead.readableNow.toLocaleString('en-US')} CVs from the pool · ${jevRead.estimateLabel}`, body: 'The list re-orders as people are read.', isReading: true, buttonLabel: null };
  }
  if (jevRead.unreadMembers === 0) return { title: 'Everyone in the pool has been read for this job.', body: READ_BODY, isReading: false, buttonLabel: null };
  const buttonLabel = jevRead.readableNow > 0 ? `Read their CVs with Jev · ${jevRead.estimateLabel}` : null;
  return {
    title: `${jevRead.unreadMembers.toLocaleString('en-US')} pool ${jevRead.unreadMembers === 1 ? 'CV is' : 'CVs are'} not read yet.`,
    body: READ_BODY,
    isReading: false,
    buttonLabel,
  };
}
